import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Chip,
  Typography,
  Box,
  Divider,
  Zoom,
  CircularProgress,
} from '@material-ui/core';
import CardGiftcardIcon from '@material-ui/icons/CardGiftcard';
import { makeStyles } from '@material-ui/core/styles';

const ZoomTransition = React.forwardRef(function ZoomTransition(props, ref) {
  return <Zoom ref={ref} {...props} />;
});

const useDialogStyles = makeStyles((theme) => ({
  dialogPaper: {
    minWidth: '700px',
    maxWidth: '800px',
    borderRadius: '12px',
    position: 'relative',
    overflow: 'hidden',
  },
  statusBadge: {
    position: 'absolute',
    top: theme.spacing(2.5),
    right: theme.spacing(3),
    zIndex: 10,
    fontWeight: 600,
    textTransform: 'uppercase',
    fontSize: '0.7rem',
    height: '28px',
    borderRadius: '14px',
    letterSpacing: '0.5px',
  },
  header: {
    padding: theme.spacing(3, 3, 2, 3),
    borderBottom: `1px solid ${theme.palette.type === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'}`,
  },
  title: {
    fontSize: '1.3rem',
    fontWeight: 700,
    color: theme.palette.text.primary,
    lineHeight: 1.3,
  },
  subtitle: {
    fontSize: '0.85rem',
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5),
  },
  body: {
    padding: theme.spacing(2.5, 3),
  },
  sectionTitle: {
    fontSize: '0.75rem',
    fontWeight: 700,
    color: theme.palette.text.secondary,
    textTransform: 'uppercase',
    letterSpacing: '0.8px',
    marginBottom: theme.spacing(1.5),
  },
  infoRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(0.6, 0),
    fontSize: '0.85rem',
  },
  infoLabel: {
    color: theme.palette.text.secondary,
    fontSize: '0.83rem',
  },
  infoValue: {
    color: theme.palette.text.primary,
    fontWeight: 500,
    fontSize: '0.83rem',
    textAlign: 'right',
  },
  typeBadge: {
    fontWeight: 600,
    fontSize: '0.7rem',
    color: '#fff',
    height: '22px',
  },
  qrCard: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    padding: theme.spacing(1.5, 2),
    borderRadius: '8px',
    border: `1px solid ${theme.palette.type === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}`,
    backgroundColor: theme.palette.type === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.015)',
    marginTop: theme.spacing(1),
  },
  qrImage: {
    width: 72,
    height: 72,
    flexShrink: 0,
    borderRadius: '4px',
  },
  qrInfo: {
    flex: 1,
    minWidth: 0,
  },
  qrLabel: {
    fontSize: '0.7rem',
    fontWeight: 600,
    color: theme.palette.text.secondary,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  qrValue: {
    fontSize: '0.82rem',
    fontWeight: 500,
    color: theme.palette.text.primary,
    wordBreak: 'break-all',
    marginTop: 2,
  },
  sectionDivider: {
    margin: theme.spacing(2, 0),
    backgroundColor: theme.palette.type === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)',
  },
  dialogActions: {
    padding: theme.spacing(1.5, 3),
    borderTop: `1px solid ${theme.palette.type === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'}`,
    justifyContent: 'flex-end',
    gap: theme.spacing(1),
  },
}));

const getStatusColor = (status) => {
  const colors = {
    registered: '#ff9800',
    confirmed: '#41924E',
    cancelled: '#f44336',
  };
  return colors[status] || '#757575';
};

const getTypeColor = (type) => {
  const colors = {
    adult: '#2196f3',
    child: '#ff9800',
    employee: '#9c27b0',
  };
  return colors[type] || '#757575';
};

const capitalize = (str) => (str ? str.charAt(0).toUpperCase() + str.slice(1) : '-');
const titleize = (str) => (str ? str.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : '-');

const InfoRow = ({ label, value, classes }) => (
  <Box className={classes.infoRow}>
    <Typography component="span" className={classes.infoLabel}>{label}</Typography>
    <Typography component="span" className={classes.infoValue}>{value || '-'}</Typography>
  </Box>
);

const Section = ({ title, children, classes }) => (
  <Box>
    <Typography className={classes.sectionTitle}>{title}</Typography>
    {children}
  </Box>
);

const RunParticipantDialog = ({ open, onClose, participant, onGiveRacepack }) => {
  const classes = useDialogStyles();
  const [giving, setGiving] = useState(false);

  if (!participant) return null;

  const attrs = participant.attributes || participant;

  const handleGiveRacepack = () => {
    if (!onGiveRacepack) return;
    setGiving(true);
    onGiveRacepack(participant, () => setGiving(false));
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      classes={{ paper: classes.dialogPaper }}
      maxWidth="md"
      fullWidth
      TransitionComponent={ZoomTransition}
      transitionDuration={{ enter: 250, exit: 200 }}
    >
      {/* Status Badge */}
      <Chip
        label={capitalize(attrs.status)}
        className={classes.statusBadge}
        style={{
          backgroundColor: getStatusColor(attrs.status),
          color: '#fff',
        }}
      />

      <DialogContent style={{ padding: 0 }}>
        {/* Header */}
        <Box className={classes.header}>
          <Typography className={classes.title}>
            {attrs.full_name}
          </Typography>
          <Typography className={classes.subtitle}>
            {attrs.category_name} &middot; BIB: {attrs.bib_name}
            {attrs.bib_number ? ` (${attrs.bib_number})` : ''}
          </Typography>
        </Box>

        {/* Body */}
        <Box className={classes.body}>
          <Grid container spacing={4}>
            {/* Left Column - Personal Info */}
            <Grid item xs={12} md={6}>
              <Section title="Personal Information" classes={classes}>
                <InfoRow label="Type" classes={classes} value={
                  <Chip
                    label={capitalize(attrs.participant_type)}
                    size="small"
                    className={classes.typeBadge}
                    style={{ backgroundColor: getTypeColor(attrs.participant_type) }}
                  />
                } />
                <InfoRow label="Date of Birth" value={attrs.date_of_birth} classes={classes} />
                <InfoRow label="Gender" value={capitalize(attrs.gender)} classes={classes} />
                <InfoRow label="Blood Type" value={attrs.blood_type} classes={classes} />
                <InfoRow label="Jersey Size" value={attrs.jersey_size} classes={classes} />
                <InfoRow label="Phone" value={attrs.phone_number} classes={classes} />
                <InfoRow label="Emergency Contact" value={attrs.emergency_contact} classes={classes} />
                <InfoRow label="ID Card" value={attrs.id_card_number} classes={classes} />
                {attrs.club_registration && (
                  <InfoRow label="Club Registration" value="Yes (Hermina Club+)" classes={classes} />
                )}
              </Section>

              {(attrs.medical_history || attrs.medication_needs) && (
                <>
                  <Divider className={classes.sectionDivider} />
                  <Section title="Medical Information" classes={classes}>
                    {attrs.medical_history && (
                      <InfoRow label="Medical History" value={attrs.medical_history} classes={classes} />
                    )}
                    {attrs.medication_needs && (
                      <InfoRow label="Medication Needs" value={attrs.medication_needs} classes={classes} />
                    )}
                  </Section>
                </>
              )}
            </Grid>

            {/* Right Column - Run Info */}
            <Grid item xs={12} md={6}>
              <Section title="Run Information" classes={classes}>
                <InfoRow label="Category" value={attrs.category_name} classes={classes} />
                <InfoRow label="BIB Name" value={attrs.bib_name} classes={classes} />
                <InfoRow label="BIB Number" value={attrs.bib_number} classes={classes} />
                <InfoRow label="External BIB Number" value={attrs.external_bib_number} classes={classes} />
                <InfoRow label="Purchase Ref" value={attrs.purchase_ref} classes={classes} />
                <InfoRow label="Registered" value={attrs.created_at} classes={classes} />
                <InfoRow label="Race Pack" classes={classes} value={
                  <Chip
                    label={attrs.race_pack_status === 'taken' ? 'Taken' : 'Not Taken'}
                    size="small"
                    style={{
                      backgroundColor: attrs.race_pack_status === 'taken' ? '#41924E' : '#ff9800',
                      color: '#fff',
                      fontWeight: 600,
                      fontSize: '0.75rem',
                    }}
                  />
                } />
                {attrs.race_pack_taken_at && (
                  <InfoRow label="Taken At" value={attrs.race_pack_taken_at} classes={classes} />
                )}
              </Section>

              {/* QR Code Card */}
              {attrs.qr_code && (
                <>
                  <Divider className={classes.sectionDivider} />
                  <Typography className={classes.sectionTitle}>QR Code</Typography>
                  <Box className={classes.qrCard}>
                    {attrs.qr_code_url && (
                      <img
                        src={attrs.qr_code_url}
                        alt={attrs.qr_code}
                        className={classes.qrImage}
                      />
                    )}
                    <Box className={classes.qrInfo}>
                      <Typography className={classes.qrLabel}>Code</Typography>
                      <Typography className={classes.qrValue}>{attrs.qr_code}</Typography>
                    </Box>
                  </Box>
                </>
              )}

              {attrs.participant_type === 'employee' && (
                <>
                  <Divider className={classes.sectionDivider} />
                  <Section title="Employee Information" classes={classes}>
                    <InfoRow label="Employee Type" value={titleize(attrs.employee_type)} classes={classes} />
                    <InfoRow label="Employee ID" value={attrs.employee_id_number} classes={classes} />
                    <InfoRow label="Hospital Origin" value={attrs.hospital_origin} classes={classes} />
                  </Section>
                </>
              )}

              {attrs.participant_type === 'child' && attrs.school_grade && (
                <>
                  <Divider className={classes.sectionDivider} />
                  <Section title="Child Information" classes={classes}>
                    <InfoRow label="School Grade" value={attrs.school_grade} classes={classes} />
                  </Section>
                </>
              )}
            </Grid>
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions className={classes.dialogActions}>
        <Button onClick={onClose} color="default" variant="outlined" size="small">
          Close
        </Button>
        {attrs.race_pack_status === 'not_taken' && onGiveRacepack && (
          <Button
            onClick={handleGiveRacepack}
            variant="contained"
            size="small"
            disabled={giving}
            style={{ backgroundColor: '#41924E', color: '#fff' }}
            startIcon={giving ? <CircularProgress size={16} style={{ color: '#fff' }} /> : <CardGiftcardIcon />}
          >
            Give Racepack
          </Button>
        )}
        {attrs.edit_url && (
          <Button
            onClick={() => { window.location.href = attrs.edit_url; }}
            color="default"
            variant="outlined"
            size="small"
            style={{ color: '#ff9800', borderColor: '#ff9800' }}
          >
            Edit
          </Button>
        )}
        {attrs.show_url && (
          <Button
            onClick={() => { window.location.href = attrs.show_url; }}
            variant="contained"
            size="small"
            style={{ backgroundColor: '#41924E', color: '#fff' }}
          >
            View Full Details
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default RunParticipantDialog;
